import { Box, Button, Paper, Step, StepLabel, Stepper, Typography } from '@mui/material';
import React, { useState } from 'react';
import StatementPreviewDialog from './StatementPreviewDialog';
import type { TaxStatementBatch } from '../../data/types';
import { date } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { StatusChip } from '../../ui/primitives';

const FLOW: TaxStatementBatch['status'][] = [
  'NOT_STARTED',
  'COMPONENTS_IMPORTED',
  'GENERATED',
  'REVIEWED',
  'PUBLISHED',
];
const NEXT_LABEL: Partial<Record<TaxStatementBatch['status'], string>> = {
  NOT_STARTED: 'Import final components',
  COMPONENTS_IMPORTED: 'Generate statements',
  GENERATED: 'Mark reviewed',
  REVIEWED: 'Publish to investors',
};

export default function TaxStatementsTab() {
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [preview, setPreview] = useState<TaxStatementBatch | null>(null);
  const batches = [...scope.taxBatches].sort(
    (left, right) =>
      right.financialYear.localeCompare(left.financialYear) ||
      left.fundId.localeCompare(right.fundId),
  );
  const missingFy27 = scope.funds.filter(
    (fund) =>
      !scope.taxBatches.some(
        (batch) => batch.fundId === fund.id && batch.financialYear === 'FY2027',
      ),
  );
  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {missingFy27.map((fund) => (
        <Paper
          key={fund.id}
          variant="outlined"
          sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            {fund.code} has no FY2027 batch yet.
          </Typography>
          <Button
            size="small"
            onClick={() =>
              dispatch({ type: 'TAX_BATCH_CREATE', fundId: fund.id, financialYear: 'FY2027' })
            }
          >
            Create batch
          </Button>
        </Paper>
      ))}
      {batches.map((batch) => {
        const fund = scope.fundById.get(batch.fundId);
        const step = FLOW.indexOf(batch.status);
        return (
          <Paper key={batch.id} variant="outlined" sx={{ p: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="h6">
                  {fund?.code} · {batch.financialYear} AMMA statements{' '}
                  <StatusChip status={batch.status} />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {batch.statementCount ? `${batch.statementCount} statements` : 'Not generated'}
                  {batch.componentsSource ? ` · components: ${batch.componentsSource}` : ''}
                  {batch.publishedAt ? ` · published ${date(batch.publishedAt)}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {batch.status !== 'NOT_STARTED' && (
                  <Button onClick={() => setPreview(batch)}>Preview statements</Button>
                )}
                {NEXT_LABEL[batch.status] && (
                  <Button
                    variant={batch.status === 'REVIEWED' ? 'contained' : 'outlined'}
                    onClick={() => {
                      dispatch({ type: 'TAX_BATCH_ADVANCE', id: batch.id });
                      toast(
                        batch.status === 'REVIEWED'
                          ? 'Statements published to the investor tax centre'
                          : `${NEXT_LABEL[batch.status]}: done`,
                      );
                    }}
                  >
                    {NEXT_LABEL[batch.status]}
                  </Button>
                )}
              </Box>
            </Box>
            <Stepper activeStep={step === FLOW.length - 1 ? FLOW.length : step} sx={{ mt: 2 }}>
              {['Components', 'Generated', 'Reviewed', 'Published'].map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        );
      })}
      <StatementPreviewDialog batch={preview} onClose={() => setPreview(null)} />
    </Box>
  );
}
